<template>       
    <b-overlay :show="showoverlay">
        
        <h4 class="mt-3 mb-2">Monthly Attendance</h4>
        <p style="font-size: 15px;">View and make adjustments to employee attendance details for the selected month.</p>

        <!-- Attendance Legend -->
        <div class="d-flex flex-wrap align-items-center mt-2 mb-2 px-2 pt-2 border rounded bg-wash">
            <span v-if="roster == 1" class="px-2 py-1 border rounded d-inline-flex align-items-center text-nowrap mr-2 mb-2" :style="{color: legends.scheduled}"><b-icon icon="calendar-check-fill" class="mr-1"></b-icon> Scheduled</span>
            <span class="px-2 py-1 border rounded d-inline-flex align-items-center text-nowrap mr-2 mb-2" :style="{color: legends.present}"><b-icon icon="person-check-fill" class="mr-1"></b-icon> Present</span>
            <span class="px-2 py-1 border rounded d-inline-flex align-items-center text-nowrap mr-2 mb-2" :style="{color: legends.absent}"><b-icon icon="person-x-fill" class="mr-1"></b-icon> Absent</span>
            <span class="px-2 py-1 border rounded d-inline-flex align-items-center text-nowrap mr-2 mb-2" :style="{color: legends.late}"><b-icon icon="person-badge-fill" class="mr-1"></b-icon> Late</span>
            <span class="px-2 py-1 border rounded d-inline-flex align-items-center text-nowrap mr-2 mb-2" :style="{color: legends.early_out}"><b-icon icon="person-fill" class="mr-1"></b-icon> Early Out</span>
            <span class="px-2 py-1 border rounded d-inline-flex align-items-center text-nowrap mr-2 mb-2" :style="{color: legends.late__early_out}"><b-icon icon="person-dash-fill" class="mr-1"></b-icon> Late & Early Out</span>
            <span class="px-2 py-1 border rounded d-inline-flex align-items-center text-nowrap mr-2 mb-2" :style="{color: legends.on_leave}"><b-icon icon="person-dash-fill" class="mr-1"></b-icon> Full Day Leave</span>
            <span class="px-2 py-1 border rounded d-inline-flex align-items-center text-nowrap mr-2 mb-2" :style="{color: legends.present}"><b-icon icon="circle-half" class="mr-1"></b-icon> First Half Leave</span>
            <span class="px-2 py-1 border rounded d-inline-flex align-items-center text-nowrap mr-2 mb-2" :style="{color: legends.present}"><b-icon icon="circle-half" class="mr-1 rotate-180"></b-icon> Second Half Leave</span>
            <span class="px-2 py-1 border rounded d-inline-flex align-items-center text-nowrap mr-2 mb-2" :style="{color: legends.on_leave}"><b-icon icon="clipboard-check" class="mr-1"></b-icon> Leave Pending</span>
            <span class="px-2 py-1 border rounded d-inline-flex align-items-center text-nowrap mr-2 mb-2" :style="{color: legends.holiday}"><b-icon icon="calendar-fill" class="mr-1"></b-icon> Holiday</span>
            <span class="px-2 py-1 border rounded d-inline-flex align-items-center text-nowrap mr-2 mb-2" :style="{color: legends.off_day}"><b-icon icon="calendar-minus" class="mr-1"></b-icon> Off-Day</span>
            <span class="px-2 py-1 border rounded d-inline-flex align-items-center text-nowrap mr-2 mb-2" :style="{color: legends.overtime}"><b-icon icon="hourglass-split" class="mr-1"></b-icon> Overtime</span>
            <span class="px-2 py-1 border rounded d-inline-flex align-items-center text-nowrap mr-2 mb-2" :style="{color: legends.compensatory_off}"><b-icon icon="calendar-plus" class="mr-1"></b-icon> Compensatory Off</span>
            <span class="px-2 py-1 border rounded d-inline-flex align-items-center text-nowrap mr-2 mb-2" :style="{color: legends.unassigned}"><b-icon icon="dash-square" class="mr-1"></b-icon> Unassigned</span>
            <span class="px-2 py-1 border rounded d-inline-flex align-items-center text-nowrap mr-2 mb-2" :style="{color: legends.resolve}"><b-icon icon="pencil-square" class="mr-1"></b-icon> Click to Resolve</span>
            <span class="px-2 py-1 border rounded d-inline-flex align-items-center text-nowrap mr-2 mb-2" :style="{color: '#dc3545'}"><b-icon icon="person-dash-fill" class="mr-1"></b-icon> Attendance Without Schedule</span>
        </div>

        <div class="clearfix row py-2 mb-2 no-margin align-items-center">
            <div class="col-md-3 col-sm-6 pl-0">  
                <div class="custom-search">              
                    <b-input-group class="mb-2">
                        <b-input-group-prepend is-text>
                            <b-icon icon="search"></b-icon>
                        </b-input-group-prepend>
                        <b-form-input type="search" placeholder="Search.." v-model="search" @input="resetPagination()"></b-form-input>
                    </b-input-group>
                </div>
            </div>

            <div class="col-md-3 col-sm-6 pl-0">
                <b-input-group class="mb-2">
                    <b-input-group-prepend is-text>Work Location</b-input-group-prepend>
                    <b-form-select id="branch_id" v-model="branch_id" :options="branchOptions" @change="reloadData()" style="border-top-left-radius: 0; border-bottom-left-radius: 0;">
                        <template #first>
                            <b-form-select-option :value="null">All Work Location</b-form-select-option>
                        </template>
                    </b-form-select>
                </b-input-group>
            </div>

            <div class="col-md-3 col-sm-6 pl-0">
                <b-input-group class="mb-2">
                    <b-input-group-prepend is-text>Dept</b-input-group-prepend>
                    <b-form-select id="department_id" v-model="department_id" :options="departmentOptions" @change="reloadData()" style="border-top-left-radius: 0; border-bottom-left-radius: 0;">
                        <template #first>
                            <b-form-select-option :value="null">All Department</b-form-select-option>
                        </template>
                    </b-form-select>
                </b-input-group>
            </div>

            <div class="col-md-3 col-sm-6 pl-0 d-flex align-items-center">
                <b-input-group class="mb-2 mr-2">
                    <b-input-group-prepend is-text>Year</b-input-group-prepend>
                    <b-form-select id="year" name="year" v-model="year" :options="year_options" @change="reloadData()" style="border-top-left-radius: 0; border-bottom-left-radius: 0;"></b-form-select>
                </b-input-group>
                <b-input-group class="mb-2 mr-2">
                    <b-input-group-prepend is-text>Month</b-input-group-prepend>
                    <b-form-select id="month" name="month" v-model="month" :options="$month_options" @change="reloadData()" style="border-top-left-radius: 0; border-bottom-left-radius: 0;"></b-form-select>
                </b-input-group>
                <b-icon @click="downloadData" variant="info" icon="cloud-download" font-scale="2" class="cursor-pointer mb-2" ></b-icon>
            </div>
        </div>

        <div class="row justify-content-center no-margin">

            <div class="col-sm-12 px-0">
                <div class="w-100 d-block" style="overflow-x: auto; overflow-y: hidden; max-width: 100%;">
                    <table class="table dataTable attendance-table table-sticky text-nowrap mb-0" style="min-width: 100%; width: 100%;">
                        <thead class="table-head">
                        <tr>                            
                            <td class="">Employee</td>
                            <td class="" align="center" v-for="(day_details , n) in week_days_header" :key="n" v-bind:style="{ backgroundColor: (day_details.holiday ? head_color['holiday'] : '') }">
                                <div :title="day_details.title" v-b-tooltip>{{ n }}</div>
                            </td>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(attendance , temp) in paginatedData" :key="attendance.id">
                            <td>{{ attendance.name }} <br/> {{ attendance.employee_code }} </td>
                            <td class="text-center" v-for="(day_details , d) in week_days_header" :key="d" align="center">
                              <template v-if="!moment({ year: year, month: month - 1, day: d}).isAfter(moment(), 'day')">
                                  <template v-if="attendance.attendances_list_by_day[d]">
                                    <template v-for="att in attendance.attendances_list_by_day[d]">
                                      <!-- Unassigned (without shift) -->
                                      <div class="cursor-pointer mb-1" @click="getHitsForUnassigned(attendance.employee_id,d,att.off, empHoliday(attendance, d), att.activity_log)" v-b-tooltip.hover.top="att.label == -1 ? 'Activity Log: ' + att.activity_log : ''" v-if="att.shift == null && !([1,2,3,4,5,6,7,8,9,10,11,12,13,14].includes(att.label))">
                                        <b-icon :icon="att.label == -1 ? 'person-dash-fill' : 'dash-square'" :style="{ color: att.label == -1 ? '#dc3545' : '#6c757d' }" class="fs-4"></b-icon>
                                      </div>

                                      <!-- Click to Resolve (Scheduled but unresolved past day) -->
                                      <div class="cursor-pointer mb-1" v-if="att.label == 0 && att.shift != null" @click="showResolve(attendance.employee_id,d,att.label)" v-b-tooltip.hover.top="'Click To Resolve'">
                                        <b-icon icon="pencil-square" class="fs-4" :style="{ color: legends.resolve }"></b-icon>
                                      </div>

                                      <!-- Leave Pending -->
                                      <div class="cursor-pointer mb-1" v-if="att.label == 7" @click="showResolve(attendance.employee_id,d,att.label)" v-b-tooltip.hover.top="'Leave Pending'">
                                        <b-icon icon="clipboard-check" class="fs-4" :style="{ color: legends.on_leave }"></b-icon>
                                      </div>

                                      <!-- Leave Rejected -->
                                      <div class="cursor-pointer mb-1" v-if="att.label == 8" @click="showResolve(attendance.employee_id,d,att.label)" v-b-tooltip.hover.top="'Leave Rejected'">
                                        <b-icon icon="clipboard-x" class="fs-4" :style="{ color: legends.on_leave }"></b-icon>
                                      </div>

                                      <!-- Swapped -->
                                      <div class="cursor-pointer mb-1" v-if="att.label == 13" v-b-tooltip.hover.top="'Swapped'">
                                        <b-icon icon="arrow-down-up" class="fs-4" :style="{ color: legends.swapped }"></b-icon>
                                      </div>

                                      <!-- Present -->
                                      <div class="cursor-pointer mb-1" @click="showActivity(att, attendance.employee_id)" v-if="[1,11].includes(att.label)"
                                           v-b-tooltip.hover.top="att.check_in + ' to ' + att.check_out + '(' + att.label_text + ')'">
                                        <b-icon icon="person-check-fill" class="fs-4" :style="{ color: legends.present }"></b-icon>
                                      </div>

                                      <!-- Late -->
                                      <div class="cursor-pointer mb-1" @click="showActivity(att, attendance.employee_id)" v-if="att.label == 3"
                                           v-b-tooltip.hover.top="att.check_in + ' to ' + att.check_out + '(' + att.label_text + ')'">
                                        <b-icon icon="person-badge-fill" class="fs-4" :style="{ color: legends.late }"></b-icon>
                                      </div>

                                      <!-- Early Out -->
                                      <div class="cursor-pointer mb-1" @click="showActivity(att, attendance.employee_id)" v-if="att.label == 4"
                                           v-b-tooltip.hover.top="att.check_in + ' to ' + att.check_out + '(' + att.label_text + ')'">
                                        <b-icon icon="person-fill" class="fs-4" :style="{ color: legends.early_out }"></b-icon>
                                      </div>

                                      <!-- Late & Early Out -->
                                      <div class="cursor-pointer mb-1" @click="showActivity(att, attendance.employee_id)" v-if="att.label == 5"
                                           v-b-tooltip.hover.top="att.check_in + ' to ' + att.check_out + '(' + att.label_text + ')'">
                                        <b-icon icon="person-dash-fill" class="fs-4" :style="{ color: legends.late__early_out }"></b-icon>
                                      </div>

                                      <!-- Absent -->
                                      <div class="cursor-pointer mb-1" v-if="att.label == 2" v-b-tooltip.hover.top="'Absent'">
                                        <b-icon icon="person-x-fill" class="fs-4" :style="{ color: legends.absent }"></b-icon>
                                      </div>

                                      <!-- On Leave (and half-leaves) -->
                                      <div @click="showActivity(att, attendance.employee_id)" class="cursor-pointer mb-1" v-if="att.label == 6" v-b-tooltip.hover.top="att.label_text == 'x' ? 'On Leave' : att.label_text">
                                        <b-icon v-if="att.label_text == 'On Leave' || att.label_text == 'x'" icon="person-dash-fill" class="fs-4" :style="{ color: legends.on_leave }"></b-icon>
                                        <b-icon v-else-if="att.label_text == 'First Half Leave'" icon="circle-half" class="fs-4" :style="{ color: legends.present }"></b-icon>
                                        <b-icon v-else-if="att.label_text == 'Second Half Leave'" icon="circle-half" class="fs-4 rotate-180" :style="{ color: legends.present }"></b-icon>
                                      </div>

                                      <!-- Off Day -->
                                      <div class="cursor-pointer mb-1" @click="assignToShift(attendance.employee_id,d,att.off, empHoliday(attendance, d),true)" v-if="att.label == 10" v-b-tooltip.hover.top="'Off Day'">
                                        <b-icon icon="calendar-minus" class="fs-4" :style="{ color: legends.off_day }"></b-icon>
                                      </div>

                                      <!-- Compensatory Off Day -->
                                      <div class="cursor-pointer mb-1" v-if="att.label == 12" v-b-tooltip.hover.top="'Compensatory Off Day'">
                                        <b-icon icon="calendar-plus" class="fs-4" :style="{ color: legends.compensatory_off }"></b-icon>
                                      </div>

                                      <!-- Holiday -->
                                      <div class="cursor-pointer mb-1" @click="assignToShift(attendance.employee_id,d,att.off, empHoliday(attendance, d),true)" v-if="att.label == 9" v-b-tooltip.hover.top="'Holiday'">
                                        <b-icon icon="calendar-fill" class="fs-4" :style="{ color: legends.holiday }"></b-icon>
                                      </div>

                                      <!-- Overtime -->
                                      <div class="cursor-pointer mb-1" @click="showActivity(att, attendance.employee_id)" v-if="att.label == 14 && (att.check_in != '' || att.check_out != '')" v-b-tooltip.hover.top="att.check_in + ' to ' + att.check_out + '(' + att.label_text + ')'">
                                        <b-icon icon="hourglass-split" class="fs-4" :style="{ color: legends.overtime }"></b-icon>
                                      </div>
                                      <div class="cursor-pointer mb-1" v-if="att.label == 14 && (att.check_in == '' && att.check_out == '')" v-b-tooltip.hover.top="'Overtime (' + att.shift.name + ')'" @click="showResolve(attendance.employee_id,d,att.label)">
                                        <b-icon icon="hourglass-split" class="fs-4" :style="{ color: legends.overtime }"></b-icon>
                                      </div>
                                    </template>
                                  </template>
                                  <template v-else>
                                      <div class="cursor-pointer mb-1" @click="getHitsForUnassigned(attendance.employee_id,d,false, empHoliday(attendance, d))" v-b-tooltip.hover.top="'Unassigned'">
                                        <b-icon icon="dash-square" class="fs-4" :style="{ color: legends.unassigned }"></b-icon>
                                      </div>
                                  </template>
                              </template>
                              <template v-else>
                                <template v-if="attendance.attendances_list_by_day[d]">
                                    <template v-for="att in attendance.attendances_list_by_day[d]">
                                      <!-- Unassigned (without shift) -->
                                      <div class="cursor-pointer mb-1" @click="getHitsForUnassigned(attendance.employee_id,d,att.off, empHoliday(attendance, d), att.activity_log)" v-b-tooltip.hover.top="att.label == -1 ? 'Activity Log: ' + att.activity_log : 'Unassigned'" v-if="att.shift == null && !([1,2,3,4,5,6,7,8,9,10,11,12,13,14].includes(att.label))">
                                        <b-icon :icon="att.label == -1 ? 'person-dash-fill' : 'dash-square'" :style="{ color: att.label == -1 ? '#dc3545' : '#6c757d' }" class="fs-4"></b-icon>
                                      </div>

                                      <!-- Scheduled (Future days, label 0 and shift exists) -->
                                      <div class="cursor-pointer mb-1" v-if="att.label == 0 && att.shift != null" v-b-tooltip.hover.top="roster == 1 ? att.shift.name + '(Shift Time: ' + moment(att.shift.start_time, 'HH:mm:ss').format('hh:mm A') + ' to ' + moment(att.shift.end_time, 'HH:mm:ss').format('hh:mm A') + ')' : ''">
                                        <b-icon :icon="roster == 1 ? 'calendar-check-fill' : 'dash-square'" class="fs-4" :style="{color: legends.scheduled}"></b-icon>
                                      </div>

                                      <!-- Leave Pending -->
                                      <div class="cursor-pointer mb-1" v-if="att.label == 7" v-b-tooltip.hover.top="'Leave Pending'">
                                        <b-icon icon="clipboard-check" class="fs-4" :style="{ color: legends.on_leave }"></b-icon>
                                      </div>

                                      <!-- Leave Rejected -->
                                      <div class="cursor-pointer mb-1" v-if="att.label == 8" v-b-tooltip.hover.top="'Leave Rejected'">
                                        <b-icon icon="clipboard-x" class="fs-4" :style="{ color: legends.on_leave }"></b-icon>
                                      </div>

                                      <!-- Swapped -->
                                      <div class="cursor-pointer mb-1" v-if="att.label == 13" v-b-tooltip.hover.top="'Swapped'">
                                        <b-icon icon="arrow-down-up" class="fs-4" :style="{ color: legends.swapped }"></b-icon>
                                      </div>

                                      <!-- Present -->
                                      <div class="cursor-pointer mb-1" @click="showActivity(att, attendance.employee_id)" v-if="[1,11].includes(att.label)"
                                           v-b-tooltip.hover.top="att.check_in + ' to ' + att.check_out + '(' + att.label_text + ')'">
                                        <b-icon icon="person-check-fill" class="fs-4" :style="{ color: legends.present }"></b-icon>
                                      </div>

                                      <!-- Late -->
                                      <div class="cursor-pointer mb-1" @click="showActivity(att, attendance.employee_id)" v-if="att.label == 3"
                                           v-b-tooltip.hover.top="att.check_in + ' to ' + att.check_out + '(' + att.label_text + ')'">
                                        <b-icon icon="person-badge-fill" class="fs-4" :style="{ color: legends.late }"></b-icon>
                                      </div>

                                      <!-- Early Out -->
                                      <div class="cursor-pointer mb-1" @click="showActivity(att, attendance.employee_id)" v-if="att.label == 4"
                                           v-b-tooltip.hover.top="att.check_in + ' to ' + att.check_out + '(' + att.label_text + ')'">
                                        <b-icon icon="person-fill" class="fs-4" :style="{ color: legends.early_out }"></b-icon>
                                      </div>

                                      <!-- Late & Early Out -->
                                      <div class="cursor-pointer mb-1" @click="showActivity(att, attendance.employee_id)" v-if="att.label == 5"
                                           v-b-tooltip.hover.top="att.check_in + ' to ' + att.check_out + '(' + att.label_text + ')'">
                                        <b-icon icon="person-dash-fill" class="fs-4" :style="{ color: legends.late__early_out }"></b-icon>
                                      </div>

                                      <!-- Absent -->
                                      <div class="cursor-pointer mb-1" v-if="att.label == 2" v-b-tooltip.hover.top="'Absent'">
                                        <b-icon icon="person-x-fill" class="fs-4" :style="{ color: legends.absent }"></b-icon>
                                      </div>

                                      <!-- On Leave (and half-leaves) -->
                                      <div @click="showActivity(att, attendance.employee_id)" class="cursor-pointer mb-1" v-if="att.label == 6" v-b-tooltip.hover.top="att.label_text == 'x' ? 'On Leave' : att.label_text">
                                        <b-icon v-if="att.label_text == 'On Leave' || att.label_text == 'x'" icon="person-dash-fill" class="fs-4" :style="{ color: legends.on_leave }"></b-icon>
                                        <b-icon v-else-if="att.label_text == 'First Half Leave'" icon="circle-half" class="fs-4" :style="{ color: legends.present }"></b-icon>
                                        <b-icon v-else-if="att.label_text == 'Second Half Leave'" icon="circle-half" class="fs-4 rotate-180" :style="{ color: legends.present }"></b-icon>
                                      </div>

                                      <!-- Off Day -->
                                      <div class="cursor-pointer mb-1" @click="assignToShift(attendance.employee_id,d,att.off, empHoliday(attendance, d),true)" v-if="att.label == 10" v-b-tooltip.hover.top="'Off Day'">
                                        <b-icon icon="calendar-minus" class="fs-4" :style="{ color: legends.off_day }"></b-icon>
                                      </div>

                                      <!-- Compensatory Off Day -->
                                      <div class="cursor-pointer mb-1" v-if="att.label == 12" v-b-tooltip.hover.top="'Compensatory Off Day'">
                                        <b-icon icon="calendar-plus" class="fs-4" :style="{ color: legends.compensatory_off }"></b-icon>
                                      </div>

                                      <!-- Holiday -->
                                      <div class="cursor-pointer mb-1" @click="assignToShift(attendance.employee_id, d, att.off, empHoliday(attendance, d), true)" v-if="att.label == 9" v-b-tooltip.hover.top="'Holiday'">
                                        <b-icon icon="calendar-fill" class="fs-4" :style="{ color: legends.holiday }"></b-icon>
                                      </div>

                                      <!-- Overtime -->
                                      <div class="cursor-pointer mb-1" @click="showActivity(att, attendance.employee_id)" v-if="att.label == 14 && (att.check_in != '' || att.check_out != '')" v-b-tooltip.hover.top="att.check_in + ' to ' + att.check_out + '(' + att.label_text + ')'">
                                        <b-icon icon="hourglass-split" class="fs-4" :style="{ color: legends.overtime }"></b-icon>
                                      </div>
                                      <div class="cursor-pointer mb-1" v-if="att.label == 14 && (att.check_in == '' && att.check_out == '')" v-b-tooltip.hover.top="'Overtime (' + att.shift.name + ')'" @click="showResolve(attendance.employee_id,d,att.label)">
                                        <b-icon icon="hourglass-split" class="fs-4" :style="{ color: legends.overtime }"></b-icon>
                                      </div>
                                    </template>
                                  </template>
                                  <template v-else>
                                      <div class="cursor-pointer mb-1" @click="getHitsForUnassigned(attendance.employee_id,d,false, empHoliday(attendance, d))" v-b-tooltip.hover.top="'Unassigned'">
                                        <b-icon icon="dash-square" class="fs-4" :style="{ color: legends.unassigned }"></b-icon>
                                      </div>
                                  </template>
                              </template>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div v-if="paginatedData.length == 0" class="px-3 py-3 no-result-cell">{{blankmsg}}</div>
                <div v-else class="px-3">
                    <div class="row py-2 no-margin" v-if="paginatedData.length > 0">
                        <div class="col-md-12 p-0 no-margin" v-if="paginatedData.length > 0">
                            <ul class="pagination b-pagination float-left mb-0 mt-1">
                                <li class="page-item disabled m-2">View</li>
                                <li class="page-item disabled"><b-form-select v-model="temp_length" :options="length_options" @change="setFirstPage()"></b-form-select></li>
                                <li class="page-item disabled m-2">per page. Total {{attendances.length}} records found.</li>
                            </ul>
                            <b-pagination-nav class="float-right mt-1" v-model="pagination.currentPage" :number-of-pages="pagination.pages" prev-text="Previous" next-text="Next" base-url="#" first-number last-number ></b-pagination-nav>
                        </div>
                    
                    </div>    
                </div>                
                </div>
            </div>
        </div>

      <b-modal id="activity_logModal" size="xl" scrollable  hide-footer title="Attendance Info">
          <div class="row no-margin">
              <div class="col-md-6">
                  <div class="card punch-status">
                      <div class="card-body">
                          <h5 class="card-title">Timesheet <small class="text-muted">{{period_date}}</small></h5>
                        <p class="" v-if="shift">
                          <!-- {{ roster == 1 ? 'Shift' : 'Office Time' }}: -->
                          <small class="text-muted p-2" style="border-radius: 6px;" :style="{background: shift.color + '40', color:shift.color}">
                            {{ shift.name }}
                            ({{ moment(shift.start_time, 'HH:mm').format('hh:mm A') }} -
                            {{ moment(shift.end_time, 'HH:mm').format('hh:mm A') }})
                          </small>
                        </p>
                        <div class="punch-det">
                              <h6>Punch In at</h6>
                              <p>{{check_in}}</p>
                          </div>
                          <div class="punch-info">
                              <div class="punch-hours">
                                  <span>{{work_time}}</span>
                              </div>
                          </div>
                          <div class="punch-det">
                              <h6>Punch Out at</h6>
                              <p>{{check_out}}</p>
                          </div>
                      </div>
                  </div>
                   <roster-attendance-updater @onUpdate="getEmployeesAttendance(); $bvModal.hide('activity_logModal')" 
                        :date="period_date" :employee_id="employee_id"></roster-attendance-updater>
              </div>
              <div class="col-md-6">
                  <div class="card recent-activity">
                      <div class="card-body">
                          <h5 class="card-title">Activity</h5>
                          <ul class="res-activity-list" v-if="activity_log">
                              <li v-for="(activity, index) in activity_log">
                                  <p class="mb-0" v-if="index % 2 == 0">Punch In at</p>
                                  <p class="mb-0" v-else>Punch Out at</p>
                                  <p class="res-activity-time">
                                      <i class="far fa-clock"></i>
                                      {{formateMe(activity) }}
                                  </p>
                              </li>
                          </ul>
                      </div>
                  </div>
                <roster-outside-activity :employee_id="employee_id" :attendances_date="period_date"></roster-outside-activity>
              </div>
          </div>
      </b-modal>
      <b-modal id="edit_Modal" size="lg" scrollable  hide-footer title="Attendance Info">
        <template #modal-title>
          <h5 class="card-title">Attendance Date: <small class="text-muted">{{edit_date}}</small></h5>
        </template>
        <employee-public-info  :hidebacknext="true"></employee-public-info>
          <div class="row no-margin mt-3">
              <div class="col-md-6 pr-2">
                  <div class="btn btn-primary cursor-pointer w-100" v-if="!has_pending_leave" @click="addLeave()">Add Leave</div>
              </div>
              <div class="col-md-6 pl-2">
                  <div class="btn btn-primary cursor-pointer w-100" @click="workedOffsite()">Resolve Attendance</div>
              </div>
          </div>
      </b-modal>
      <b-modal id="addLeave_Modal" size="xl" scrollable  hide-footer title="Leave Details">
          <roster-employee-leave-details v-if="employee_id > 0"  :period_date="edit_date" v-on:childToParent="reloadData"></roster-employee-leave-details>
      </b-modal>
      <b-modal id="workedOffsite_Modal" size="xl" scrollable  hide-footer :title="'Resolve Attendance: ' + edit_date">
          <roster-worked-offsite :roster="roster" v-if="employee_id > 0" :attendance_date="edit_date" v-on:childToParent="reloadData"></roster-worked-offsite>
      </b-modal>
      
      <b-modal id="assignToShift_Modal" size="md" scrollable  hide-footer :title="'Create Direct Assignment at ' + edit_date">
        <roster-manual-shift-assignment :off="off" :holiday="holiday" :newHits="newHits" :edit_date="edit_date" @closeModal="closeModal"></roster-manual-shift-assignment>
      </b-modal>
        
    </b-overlay>
</template>
<script>
    import moment from 'moment'
    export default{
        
        mounted() {
            if(this.check_ce == 1)
            {                
                if(this.ce_flg == 1)
                    this.ce = true;
                else
                    this.ce = false;
            }   
        },
        props: ['roster'],
        components: { },
        data(){            
            const now = new Date(); 
            return {
            year_options:[],
              showoverlay : false,
              empfile : null,
              shift : null,
              has_file : false,
              attendances:[],
              week_days_header:[],
              legends: {},
              head_color : {weekend : '#aaaaaa' , holiday : '#aaaaaa'},
              search:'',
              length: 50,
              temp_length: 50,
              off:false,
              holiday: '',
              is_off_day_assign: false,
              length_options: [],
              pagination: {currentPage: 1,total: '',nextPage: '',prevPage: '',from: '',to: '', pages:1},
              ce : true,
              today : 0,
              grace_time_late_check_in : 0,
              grace_time_early_checkout : 0,
              blankmsg : 'Fetching data..',
              errormsg : [],
              can_download_template : true,
              period_date : null,
              check_in : null,
              check_out : null,
              work_time : 0,
              break_time : 0,
              over_time : 0,
              activity_log : null,
              edit_date : null,
              has_pending_leave: false,
              employee_id : 0,
              month: ('0' + (new Date().getMonth() + 1)).slice(-2),
              year: now.getFullYear(),
              newHits: '',
              legend : {'absent':'#DE0000' , 'late_early': '#BF8F00' , 'ontime': '#18A022' , 'auto_checkout':'#DE0000'},
              branch_id: null,
              department_id: null,
              branchData: [],
              departmentData: []
            };
        },
        methods:{
            moment,

            getLeaveYears() {
                axios
                .get(this.$api_url + "/leave-attendance/leave-years/list")
                .then((response) => {
                this.year_options = response.data;
                });
            },

            empHoliday(attendance, d) {
                return (attendance.holiday_by_day && attendance.holiday_by_day[d]) || null;
            },

            getHitsForUnassigned(employee_id, d, off = false, day_details = null, hits = null) {
                this.newHits = hits;
                this.assignToShift(employee_id, d, off, day_details, false);
            },

          closeModal(errors = null){
            if(errors == null){
              this.$bvToast.toast('Assigned Successfully', {
                variant: 'success',
                title: 'Success',
                solid: true,
              });
            } else {
              this.errors = errors;
            }
            this.reloadData();
            this.$bvModal.hide('assignToShift_Modal');
          },
            /********************* */
            reloadData: function()
            {
                this.$bvModal.hide('edit_Modal');
                this.$bvModal.hide('addLeave_Modal');
                this.$bvModal.hide('workedOffsite_Modal');
                this.getEmployeesAttendance();
            },
            resetdata : function()
            {
                this.period_date = null;
                this.check_in = null;
                this.check_out = null;
                this.work_time = 0;
                this.break_time = 0;
                this.over_time = 0;
            },
            formateMe :function(minutes){
                if(minutes == null) return '..';
                return moment(minutes, "YYYY-mm-dd hh:mm:ss").format('LT');
            },
            showActivity : function(ins, employee_id)
            {                
              this.resetdata();

              this.period_date = ins.period_date;
              this.check_in = ins.check_in;
              this.check_out = ins.check_out;
                                
              this.work_time = ins.work_time;
              this.break_time = ins.break_time;
              this.over_time = ins.over_time;
              if(ins.activity_log == null) {
                return;
              }
              this.activity_log = (ins.activity_log.split(',') || []).slice().sort((a, b) => {
                return new Date(a.time) - new Date(b.time);
              });
              this.employee_id = employee_id;
              this.shift = ins.shift;

              this.$bvModal.show('activity_logModal');

            },
            showResolve : function(employee_id , d, label)
            {
                this.resetdata();    
                this.setEmployee(employee_id).then(r => {
                    this.edit_date = moment({
                        year: parseInt(this.year),
                        month: parseInt(this.month) - 1,
                        day: d
                    }).format('YYYY-MM-DD');
                    this.employee_id = employee_id;
                    this.has_pending_leave = label == 7;
                    this.$bvModal.show('edit_Modal');      
                });
                          
            },
            addLeave : function()
            {
                this.$bvModal.hide('edit_Modal');
                this.$bvModal.show('addLeave_Modal');
            },
            workedOffsite : function(date)
            {
                this.$bvModal.hide('edit_Modal');
                this.$bvModal.show('workedOffsite_Modal');
            },
            assignToShift: function(employee_id, d, off = false, holiday = null, is_off_day = false)
            {
                this.off = !!off;
                if(holiday != null) {
                    if(holiday.holiday) {
                        this.holiday = holiday.title;
                    } else {
                        this.holiday = '';
                    }
                } else {
                    this.holiday = '';
                }
                
                this.is_off_day_assign = is_off_day;

                this.resetdata();

                this.setEmployee(employee_id).then(r => {
                    this.edit_date = moment({
                        year: parseInt(this.year),
                        month: parseInt(this.month) - 1,
                        day: d
                    }).format('YYYY-MM-DD');

                    this.employee_id = employee_id;
                    this.$bvModal.hide('edit_Modal');
                    this.$bvModal.show('assignToShift_Modal');
                });
            },
            setEmployee: async function(x) 
            {
                let currentObj = this;
                let formData = new FormData();
                formData.append('employee_id', x);
                await axios.post(this.$api_url+'/employee/setemployee', formData)
                    .then(function (response) 
                    {    
                       return;
                        
                    }.bind(this))
                    .catch(function (error) 
                    {
                        currentObj.showoverlay = false;
                        currentObj.serverError(error);
                    });
            },

            getEmployeesAttendance: function()
            {                
                if(!this.checkNet()) return false;
                let currentObj = this;

                this.showoverlay = true;

                var today = new Date();

                let month = this.month;
                let year = this.year;
                let fiscal_year = this.getCurrentFinancialYear();
                let years = fiscal_year.split('-');
                this.attendances = [];
                currentObj.showoverlay = true;
                axios.get(this.$api_url+'/leave-attendance/attendance/month' , {
                    params: {
                        month: month,
                        year: year,
                        export_flg : 0,
                        branch_id: this.branch_id,
                        department_id: this.department_id,
                    }
                }).then(function(response){
                    this.attendances = response.data.attendances_list;
                    this.legends = response.data.legends;

                    if(this.attendances.length == 0)
                        this.blankmsg = "Looks like you have not added any employee attendance yet for the selected month.";


                    this.week_days_header = response.data.week_days_header;
                    this.head_color = response.data.head_color;

                    this.length_options = [{ value: 10, text: "10" },{ value: 20, text: "20" },
                                                    { value: 50, text: "50" },
                                                    { value: 100, text: "100" },
                                                    { value: 200, text: "200" },
                                                    { value: 500, text: "500" }, 
                                                    { value: this.attendances.length, text: "All" }
                                                ]; 
                    this.people = this.attendances.length;

                    this.today = response.data.today;

                    this.grace_time_late_check_in = response.data.grace_time_late_check_in;
                    this.grace_time_early_checkout = response.data.grace_time_early_checkout;

                    currentObj.showoverlay = false;
                    
                }.bind(this)).catch(function (error){ 
                        currentObj.showoverlay = false;
                        currentObj.serverError(error);});

                
            },
            setFirstPage :  function()
            {  
                this.pagination.currentPage = 1;                
                this.length = this.temp_length;   
            },

            downloadData : function()
            {
                if(this.attendances.length > 0)
                {
                    if(!this.checkNet()) return false;
                    this.showoverlay = true;
                    axios.get(this.$api_url+'/leave-attendance/attendance/month' , {
                        params: {
                            month: this.month,
                            year: this.year,
                            export_flg : 1,
                            branch_id: this.branch_id,
                            department_id: this.department_id,
                        },
                      responseType:'blob'
                    }).then((response) => {
                      this.global_download_file_content(response);
                      this.showoverlay = false;
                    }).catch((error) => {
                      this.serverError(error);
                      this.showoverlay = false;
                    });
                }
            },

            loadFilterData: function() {
                axios.get(this.$api_url + '/branch/list').then(response => {
                    this.branchData = response.data;
                });
                axios.get(this.$api_url + '/department/list').then(response => {
                    this.departmentData = response.data;
                });
            }

        },
        created: function(){
            this.getLeaveYears();
            this.loadFilterData();
            this.getEmployeesAttendance();
        },
        computed: 
        {
            branchOptions() {
                return this.branchData.map(item => ({
                    value: item.id,
                    text: item.name || `Work Location ${item.id}`
                }));
            },
            departmentOptions() {
                return this.departmentData.map(item => ({
                    value: item.id,
                    text: item.name || `Department ${item.id}`
                }));
            },
            filteredData() 
            {
                let filtered_list = this.attendances;
                if (this.search) 
                {
                    filtered_list = filtered_list.filter((row) => {return Object.keys(row).some((key) => {return String(row[key]).toLowerCase().indexOf(this.search.toLowerCase()) > -1;})});
                }                
                return filtered_list;
            },
            paginatedData() 
            {     
                return this.paginate(this.filteredData, this.length, this.pagination.currentPage);
            }
        }
    }
</script>

